import { type ReactNode, useMemo, useState } from 'react'
import { useLocations } from '../../../features/locations/use-locations'

const serif = { fontFamily: 'var(--v3-font-serif)' } as const
const mono = { fontFamily: 'var(--v3-font-mono)' } as const

const SKELETON_ROWS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']

type Filter = 'all' | 'Active' | 'Inactive'

function Status({ status }: { status: 'Active' | 'Inactive' }) {
  const on = status === 'Active'
  return (
    <span
      className="inline-flex items-center gap-2 text-[13px]"
      style={{ color: on ? 'var(--v3-ok)' : 'var(--v3-off)', ...serif, fontStyle: on ? 'normal' : 'italic' }}
    >
      <span
        aria-hidden="true"
        className="size-[7px] rounded-full"
        style={on ? { background: 'var(--v3-ok)' } : { boxShadow: 'inset 0 0 0 1.5px var(--v3-off)' }}
      />
      {status}
    </span>
  )
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p
      className="border-b border-[var(--color-border)] py-10 text-center text-[16px] italic text-[var(--color-muted)]"
      style={serif}
    >
      {children}
    </p>
  )
}

export function LedgerLocations() {
  const { data, isLoading, error } = useLocations()
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<Filter>('all')

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (data ?? []).filter(
      (r) =>
        (filter === 'all' || r.status === filter) &&
        (!needle || r.code.toLowerCase().includes(needle) || r.name.toLowerCase().includes(needle)),
    )
  }, [data, q, filter])

  const total = data?.length ?? 0
  const active = data?.filter((r) => r.status === 'Active').length ?? 0

  return (
    <article>
      <header className="border-b-2 border-[var(--v3-rule-strong)] pb-6">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-accent)]">
          Directory
        </p>
        <h1 className="text-[36px] font-semibold leading-[1.1] tracking-[-0.015em]" style={serif}>
          Locations
        </h1>
        <p className="mt-3 max-w-[56ch] text-[16px] leading-[1.55] text-[var(--color-muted)]" style={serif}>
          Every store on the network, by Aptos code &mdash; a plain register of what is open for business and what is
          not.
        </p>
      </header>

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[var(--color-border)] py-4">
        <p className="text-[13px] text-[var(--color-muted)]" aria-live="polite">
          {isLoading || error ? (
            ' '
          ) : (
            <>
              <span className="text-[var(--color-fg)]">{rows.length.toLocaleString('en-US')}</span> of{' '}
              {total.toLocaleString('en-US')} locations, {active.toLocaleString('en-US')} active
            </>
          )}
        </p>
        <div className="flex items-end gap-6">
          <fieldset aria-label="Filter by status" className="m-0 flex min-w-0 gap-4 border-0 p-0 text-[13px]">
            {(['all', 'Active', 'Inactive'] as const).map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
                className={`-mb-px cursor-pointer border-b-2 pb-1 capitalize transition-colors focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] ${
                  filter === f
                    ? 'border-[var(--color-accent)] font-semibold text-[var(--color-fg)]'
                    : 'border-transparent text-[var(--color-muted)] hover:text-[var(--color-fg)]'
                }`}
              >
                {f}
              </button>
            ))}
          </fieldset>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search code or name"
            aria-label="Search locations"
            className="w-[220px] border-0 border-b border-[var(--color-border)] bg-transparent pb-1 text-[13.5px] text-[var(--color-fg)] placeholder:italic placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
          />
        </div>
      </div>

      {error ? (
        <Note>The ledger could not be read: {error.message}</Note>
      ) : isLoading ? (
        <output aria-busy="true" aria-label="Loading locations" className="block divide-y divide-[var(--color-border)]">
          {SKELETON_ROWS.map((k) => (
            <div key={k} className="flex h-10 items-center gap-8">
              <span className="v3-skeleton w-14" />
              <span className="v3-skeleton w-48" />
              <span className="v3-skeleton ml-auto w-16" />
            </div>
          ))}
        </output>
      ) : rows.length === 0 ? (
        <Note>{total === 0 ? 'No locations on record.' : 'Nothing matches that search.'}</Note>
      ) : (
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-[var(--v3-rule-strong)] text-[12px] text-[var(--color-muted)]">
              <th scope="col" className="w-[120px] py-2.5 pr-4 font-medium">
                Code
              </th>
              <th scope="col" className="py-2.5 pr-4 font-medium">
                Name
              </th>
              <th scope="col" className="w-[140px] py-2.5 text-right font-medium">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={`${r.code}-${i}`}
                className="h-10 border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface)]"
              >
                <td className="pr-4 text-[13px] tabular-nums text-[var(--color-muted)]" style={mono}>
                  {r.code}
                </td>
                <td className="pr-4 text-[15px]" style={serif}>
                  {r.name}
                </td>
                <td className="text-right">
                  <Status status={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  )
}
